import { message, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import { useRef } from 'react';

import { AddDocumentIcon, DeleteDocIcon } from '../../assets';
import CancelIcon from '../../assets/CancelIcon';
import InfoFilled from '../../assets/doccompare/InfoFilled';
import DocIcon from '../../assets/DocIcon';
import PdfIcon from '../../assets/PdfIcon';
import UploadIcon from '../../assets/UploadIcon';
import WrongIcon from '../../assets/WrongIcon';
import { acceptTypes } from '../../Constants';
import { DOCCOMPARE_MAX_FILES } from '../../Constants/commonConstants';

function InputUpload2({ fileStatus, setFileStatus, uploadData, setUploadData }) {
  const fileRef = useRef();

  const handleEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.backgroundImage =
      "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23007FFFFF' stroke-width='2' stroke-dasharray='7%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")";
  };

  const handleLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.backgroundImage = 'none';
  };

  const handleUpload = (e, setFieldValue) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer?.files || e.target.files;

    if (!files || files.length === 0) {
      message.error('No files were uploaded.');
      return;
    }

    // Set the first file in the form field
    setFieldValue('file', files);

    // Optional: reset background styling after upload
    e.currentTarget.style.backgroundImage = 'none';
  };

  const handleDeselection = (el) => {
    const temp = uploadData?.filter((itm) => itm !== el);
    setUploadData(temp);
  };

  const validateFiles = (values) => {
    const errors = {};
    const validTypes = ['doc', 'docx', 'dot', 'dotx', 'docm', 'dotm', 'pdf'];
    const maxFiles = DOCCOMPARE_MAX_FILES;

    // Check if files exist
    if (!values?.file || values.file.length === 0) {
      return errors;
    }

    const fileArr = Array.from(values.file);

    // Validate file count
    if (fileArr.length + uploadData?.length > maxFiles) {
      errors.file = `Too many files. You can upload up to ${maxFiles} documents.`;
    }

    // Validate file types
    const invalidFiles =
      !errors.file &&
      fileArr.filter((file) => !validTypes.includes(file.name.split('.').pop()?.toLowerCase()));

    if (invalidFiles.length > 0 && !errors.file) {
      errors.file = 'Invalid file type(s) detected. Please upload valid documents.';
    }

    // If no errors, add the files to the uploadData
    if (!errors.file) {
      setFileStatus(true);
      setUploadData([...uploadData, ...fileArr]);
    } else {
      setFileStatus(false);
    }
    return errors;
  };

  return (
    <div
      className='form-wrapper'
      role='button'
      onClick={() => uploadData?.length === 0 && fileRef.current.click()}
    >
      {fileStatus && uploadData?.length === 1 && (
        <div className='flex align-center' style={{ gap: '8px', justifyContent: 'center' }}>
          <InfoFilled /> <span className='fs-14'> Upload 1 more file to start comparing</span>
        </div>
      )}

      <Formik initialValues={{ file: null }} validate={validateFiles}>
        {({ errors, setFieldValue }) => (
          <div
            className='drop-area'
            role='button'
            onDragEnter={(e) => handleEnter(e)}
            onDragLeave={(e) => handleLeave(e)}
            onDragOver={(e) => handleOver(e)}
            onDrop={(e) => handleUpload(e, setFieldValue)}
          >
            {errors.file && (
              <button
                className='cancel-btn'
                type='reset'
                onClick={(e) => {
                  e.stopPropagation();
                  setFieldValue('file', null);
                  setUploadData([]);
                  fileRef.current.value = '';
                }}
              >
                <CancelIcon />
              </button>
            )}
            <Form className='form'>
              <div className={uploadData?.length > 0 ? 'form-inner' : null}>
                <input
                  type='file'
                  name='file'
                  onChange={(event) => {
                    setFieldValue('file', event.target.files);
                  }}
                  hidden
                  ref={fileRef}
                  multiple
                  accept={acceptTypes}
                />
                {/* Display upload button */}
                {errors.file ? (
                  <button className='upload-btn' disabled>
                    <WrongIcon />
                    <div className='error-message'>
                      <p>{errors.file}</p>
                    </div>
                  </button>
                ) : (
                  uploadData?.length === 0 && (
                    <div className='upload-btn' style={{ marginTop: '1rem' }}>
                      <h2 className='mb-8' style={{ fontSize: '20px' }}>
                        Compare Documents Like Never Before
                      </h2>
                      <p className='fs-14'>
                        Maximum file size is <strong>100MB</strong>. Please make sure the document
                        is readable to naked eyes.
                      </p>
                      <UploadIcon />
                      <div className='upload-heading'>
                        <h4>Click or Drag & Drop to Upload</h4>
                        <p className='fs-14'>2 to {DOCCOMPARE_MAX_FILES} Documents.</p>
                        <p className='mt-16 fs-14'>
                          <span className='fw-600'>Files Supported:</span> PDFs and Word
                        </p>
                      </div>
                    </div>
                  )
                )}
                {uploadData?.length > 0 && !errors.file && (
                  <div className='file-docchat'>
                    {uploadData?.length < DOCCOMPARE_MAX_FILES && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          fileRef.current.click();
                        }}
                        aria-hidden='true'
                        className='add-document-docchat'
                      >
                        <AddDocumentIcon />
                        Add Document
                      </div>
                    )}
                    {uploadData.map((el) => (
                      <div className='file-title-docchat' key={el.lastModified}>
                        <div className='file-deselection'>
                          {el?.name?.includes('.pdf') ? (
                            <PdfIcon size={24} />
                          ) : (
                            <DocIcon size={24} />
                          )}
                          <span onClick={() => handleDeselection(el, setFieldValue)}>
                            <DeleteDocIcon />
                          </span>
                        </div>
                        <h4 className='uploaded-file-name-docchat'>
                          <Tooltip placement='bottom' title={el?.name} arrow>
                            {el?.name}
                          </Tooltip>
                        </h4>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
}

export default InputUpload2;
